import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Box, Typography } from "@mui/material";

import useLocalization from "../../hooks/useLocalization";
import useSyndication from "../../hooks/useSyndication";
import useEntity from "../../hooks/useEntity";
import useData from "../../hooks/useData";
import useBrowser from "../../hooks/useBrowser";

import Input from "../elements/Input";
import EditorToolbar from "./EditorToolbar";
import Switch from "../elements/Switch";
import AutoComplete from "../elements/AutoComplete";
import Date from "../elements/Date";
import Select from "../elements/Select";

const ProjectEditor = ({ projectId, clientId, onSave }) => {
  const navigate = useNavigate();
  const { syndication } = useSyndication();
  const { windowHeight } = useBrowser();

  const { getLabel } = useLocalization();
  const {
    setEntity,
    emptyObject,
    entityData,
    entityName,
    clearEntityData,
    getEntityData,
    validateEntityData,
    updateField,
    message,
    isSuccessful,
    getFieldMessage,
    deleteEntity,
    saved,
    setEntityData,
    deleted,
  } = useEntity();
  const { getFromServer } = useData();

  const [users, setUsers] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [hasTouchedWeeklyExclusivity, setHasTouchedWeeklyExclusivity] =
    useState(false);

  useEffect(() => {
    setEntity("Project");
  }, []);

  useEffect(() => {
    getData();
  }, [projectId, entityName]);

  useEffect(() => {
    if (!emptyObject) return;
    if (projectId) return;

    setEntityData({ ...emptyObject, clientId });
  }, [emptyObject]);

  useEffect(() => {
    if (!entityData) return;
    if (projectId) return;
    if (hasTouchedWeeklyExclusivity) return;

    const isSyndicated = !!entityData?.syndicationId;
    const defaultWeeklyExclusivity = isSyndicated;

    if (entityData?.enforeWeeklyExclusivity !== defaultWeeklyExclusivity) {
      updateField("enforeWeeklyExclusivity", defaultWeeklyExclusivity);
    }
  }, [
    entityData?.syndicationId,
    entityData?.enforeWeeklyExclusivity,
    hasTouchedWeeklyExclusivity,
    projectId,
  ]);

  useEffect(() => {
    if (projectId) return;
    if (!entityData) return;
    if (entityData?.projectTypeId) return;

    const standardProjectType = Array.isArray(projectTypes)
      ? projectTypes.find(
          (projectType) =>
            String(projectType?.code || "")
              .trim()
              .toLowerCase() === "standard",
        )
      : null;

    if (!standardProjectType?.id) return;

    updateField("projectTypeId", standardProjectType.id);
  }, [projectId, entityData?.projectTypeId, projectTypes]);

  const getData = async () => {
    if (!entityName) return;

    // Only active admin/sysadmin users can be selected as project managers
    const userData = await getFromServer({
      entityName: "User",
      filter: { isActive: true, role: { $in: ["admin", "sysadmin"] } },
    });

    const normalizedUsers = (userData || []).map((user) => {
      const id = user.id || user._id || user.userId;
      const nameCandidate =
        user.legalName ||
        user.name ||
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        user.username ||
        user.email ||
        "(no name)";

      return {
        id,
        key: id,
        name: nameCandidate,
      };
    });

    setUsers(normalizedUsers);

    const projectTypeData = await getFromServer({
      entityName: "ProjectType",
      filter: { isActive: true },
    });

    const normalizedProjectTypes = Array.isArray(projectTypeData)
      ? projectTypeData.map((projectType) => ({
          id: projectType.id || projectType._id,
          name: projectType.name || projectType.code || "(unnamed type)",
          code: projectType.code,
        }))
      : [];

    setProjectTypes(normalizedProjectTypes);

    if (!projectId) return;

    await getEntityData(projectId);
  };

  const handleSave = async () => {
    validateEntityData();
  };

  useEffect(() => {
    if (!saved) return;
    onSave();
  }, [saved]);

  const handleDelete = async () => {
    await deleteEntity();
  };

  useEffect(() => {
    if (!deleted) return;
    navigate(`/client`, { state: { clientId } });
  }, [deleted]);

  if (!entityData) return;

  return (
    <Box
      width="100%"
      height="100%"
    >
      <EditorToolbar
        onSave={handleSave}
        onDelete={handleDelete}
        message={projectId}
      />
      <Box
        padding="10px"
        display="flex"
        flexDirection="column"
        gap={1}
        sx={{
          height: `${windowHeight - 225}px`,
          overflow: "auto",
        }}
      >
        <Input
          fullwidth
          label={getLabel("name")}
          value={entityData?.name}
          onChange={(newValue) => updateField("name", newValue)}
        />
        <Input
          fullwidth
          label={getLabel("name_fr")}
          value={entityData?.name_fr}
          onChange={(newValue) => updateField("name_fr", newValue)}
        />

        <Select
          id="syndication"
          label={getLabel("syndication")}
          value={entityData?.syndicationId || "notSyndicated"}
          onChange={(newValue) =>
            updateField(
              "syndicationId",
              newValue === "notSyndicated" ? null : newValue,
            )
          }
          items={[
            { id: "notSyndicated", name: "Not Syndicated" },
            { id: syndication?.id, name: syndication?.name },
          ]}
        />
        <Input
          fullwidth
          label={getLabel("projectCode")}
          value={entityData?.projectCode}
          onChange={(newValue) => updateField("projectCode", newValue)}
          maxLength={50}
          showLength={true}
        />

        <Select
          id="projectType"
          label="projectType"
          value={entityData?.projectTypeId || ""}
          onChange={(newValue) =>
            updateField("projectTypeId", newValue === "" ? null : newValue)
          }
          items={Array.isArray(projectTypes) ? projectTypes : []}
        />

        <AutoComplete
          id="users"
          label={getLabel("manager")}
          value={entityData?.projectManagerId}
          onChange={(newValue) => updateField("projectManagerId", newValue)}
          items={users}
          width="100%"
        />
        <Switch
          label={getLabel("isActive")}
          value={entityData?.isActive}
          onChange={(newValue) => updateField("isActive", newValue)}
        />
        <Switch
          label="Exclude From Outliers"
          value={!!entityData?.excludeFromOutliers}
          onChange={(newValue) => updateField("excludeFromOutliers", newValue)}
        />
        <Box
          display="flex"
          justifyContent="space-between"
          gap={1}
          marginTop="5px"
        >
          <Date
            label={getLabel("startsOn")}
            value={entityData?.startsOn}
            onChange={(newValue) => updateField("startsOn", newValue)}
          />
          <Date
            label={getLabel("endsOn")}
            value={entityData?.endsOn}
            onChange={(newValue) => updateField("endsOn", newValue)}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="flex-start"
          gap={1}
        >
          <Date
            label="Lock Date (optional)"
            value={entityData?.lockDate}
            onChange={(newValue) => updateField("lockDate", newValue)}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Input
            fullwidth
            label={getLabel("callDuration")}
            value={entityData?.callDuration}
            onChange={(newValue) => updateField("callDuration", newValue)}
            type="number"
          />
          <Input
            fullwidth
            label={getLabel("callVariance")}
            value={entityData?.callVariance}
            onChange={(newValue) => updateField("callVariance", newValue)}
            type="number"
          />
          <Input
            fullwidth
            label={getLabel("hourlyRate")}
            value={entityData?.hourlyRate}
            onChange={(newValue) => updateField("hourlyRate", newValue)}
            type="number"
          />
        </Box>
        <Box>
          <Input
            fullwidth
            label="Billing Call Rate"
            value={entityData?.billingCallRate ?? ""}
            onChange={(newValue) => {
              const numValue =
                newValue === "" ? 0 : Math.max(0, parseFloat(newValue) || 0);
              updateField("billingCallRate", numValue);
            }}
            type="number"
            required
            {...getFieldMessage("billingCallRate")}
          />
        </Box>
        <Box
          border="solid 1px #ccc"
          paddingLeft="5px"
          borderRadius="5px"
        >
          <Box
            display="flex"
            justifyContent="center"
          >
            <Typography variant="overline">{getLabel("repActions")}</Typography>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            gap={1}
            marginTop="-10px"
          >
            <Switch
              label={getLabel("repsCanChange")}
              value={entityData?.repsCanChange}
              onChange={(newValue) => updateField("repsCanChange", newValue)}
            />
            <Switch
              label={getLabel("repsCanAdd")}
              value={entityData?.repsCanAdd}
              onChange={(newValue) => updateField("repsCanAdd", newValue)}
            />
            <Switch
              label={getLabel("allowExpenses")}
              value={!!entityData?.allowExpenses}
              onChange={(newValue) => updateField("allowExpenses", newValue)}
            />
            <Switch
              label="Allow Duplicate Calls Per Day"
              value={!!entityData?.allowDuplicateCallsPerDay}
              onChange={(newValue) =>
                updateField("allowDuplicateCallsPerDay", newValue)
              }
            />
          </Box>
        </Box>

        {/* Rescheduling Rules Section */}
        <Box
          border="solid 1px #ccc"
          paddingLeft="5px"
          paddingRight="5px"
          borderRadius="5px"
        >
          <Box
            display="flex"
            justifyContent="center"
          >
            <Typography variant="overline">RESCHEDULING RULES</Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            marginTop="-5px"
            paddingBottom="10px"
          >
            <Switch
              label="Enforce Weekly Exclusivity"
              value={
                entityData?.enforeWeeklyExclusivity === true ||
                entityData?.enforeWeeklyExclusivity === 1
              }
              onChange={(newValue) => {
                setHasTouchedWeeklyExclusivity(true);
                updateField("enforeWeeklyExclusivity", newValue);
              }}
            />
            <Typography
              variant="caption"
              sx={{
                marginTop: "-8px",
                marginLeft: "8px",
                color: "text.secondary",
              }}
            >
              Reps cannot reschedule into a week where this location already has
              a call for the same project.
            </Typography>
            <Switch
              label="Enable Blackout Enforcement"
              value={entityData?.blackoutDays > 0}
              onChange={(newValue) =>
                updateField("blackoutDays", newValue ? 7 : 0)
              }
            />
            {entityData?.blackoutDays > 0 && (
              <Input
                fullwidth
                label="Minimum Days Between Calls"
                value={entityData?.blackoutDays}
                onChange={(newValue) => {
                  const numValue = Math.max(
                    0,
                    Math.floor(parseFloat(newValue) || 0),
                  );
                  updateField("blackoutDays", numValue);
                }}
                type="number"
                helperText="Reps cannot reschedule calls within ± this many days of existing calls (same location & project)"
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectEditor;
